import { ReactNode } from "react";
import { ArtAppLoader } from "@art/shared-ui";
import { usePermission } from "./usePermission";
import { usePermissionsContext } from "./permissions-provider";
import AccessDenied from "../../components/access-denied";

interface RequirePermissionProps {
  application: string;
  feature: string;
  category: string;
  /** Optional content to render while permissions are loading. */
  loadingFallback?: ReactNode;
  children: ReactNode;
}

/**
 * Route-level permission guard. Renders `children` only if the current user
 * has the given permission; otherwise renders the `AccessDenied` view.
 * While the permission set is still loading, shows a centered spinner so the
 * page never flashes forbidden content before the guard resolves.
 *
 * Example:
 *   <Route element={
 *     <RequirePermission application="PLATFORM_SETTINGS" feature="ROLE_MANAGEMENT" category="Read">
 *       <RolesPermissions />
 *     </RequirePermission>
 *   } />
 */
export function RequirePermission({
  application,
  feature,
  category,
  loadingFallback,
  children,
}: RequirePermissionProps) {
  const { loading } = usePermissionsContext();
  const allowed = usePermission(application, feature, category);

  if (loading) {
    return <>{loadingFallback ?? <ArtAppLoader />}</>;
  }

  return allowed ? <>{children}</> : <AccessDenied />;
}
